import { Box, Flex, Text, config as cssConfig } from "@100mslive/react-ui";
import { CloseIcon, DoorIcon } from "@100mslive/react-icons";
import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  selectAudioTrackVolume,
  selectLocalPeerID,
  selectLocalPeerRoleName,
  selectPeerScreenSharing,
  selectPeerSharingVideoPlaylist,
  selectPeers,
  selectPeersScreenSharing,
  selectScreenShareByPeerID,
  useHMSActions,
  useHMSStore,
} from "@100mslive/react-sdk";

import { APP_DATA } from "../common/constants";
import { LiveHodoContext } from "../contexts/LiveHodoContext";
import { ROLES } from "../common/roles";
import { ScreenshareDisplay } from "../components/ScreenshareDisplay";
import ScreenshareTile from "../components/ScreenshareTile";
import VideoList from "../components/VideoList";
import { VideoPlayer } from "../components/Playlist/VideoPlayer";
import VideoScreenShareList from "../components/VideoScreenShareList";
import VideoTile from "../components/VideoTile";
import { hmsStore } from "../hms.js";
import { useMedia } from "react-use";
import { useNavigation } from "../components/hooks/useNavigation";
import { useParams } from "react-router-dom";
import { useSetAppDataByKey } from "../components/AppData/useUISettings";

const ScreenShareView = ({ isCabin }) => {
  const {
    stateCtx: { selectedCabinData, selectedScreenShare },
    setSelectedScreenShare,
  } = useContext(LiveHodoContext);

  // for smaller screen we will show sidebar in bottom
  const mediaQueryLg = cssConfig.media.xl;
  const showSidebarInBottom = useMedia(mediaQueryLg);
  const peers = useHMSStore(selectPeers);
  const localPeerID = useHMSStore(selectLocalPeerID);
  const localPeerRole = useHMSStore(selectLocalPeerRoleName);
  const peerPresentingRaw = useHMSStore(selectPeerScreenSharing);
  const presenters = useHMSStore(selectPeersScreenSharing);
  const peerSharingPlaylist = useHMSStore(selectPeerSharingVideoPlaylist);
  const isPresenterFromMyRole =
    peerPresentingRaw?.roleName?.toLowerCase() === localPeerRole?.toLowerCase();
  const amIPresenting = localPeerID === peerPresentingRaw?.id;
  const showPresenterInSmallTile =
    showSidebarInBottom || amIPresenting || isPresenterFromMyRole;
  const [listPeers, setListPeers] = useState([]);
  const [peerPresenting, setPeerPresenting] = useState();

  useEffect(() => {
    // console.log(presenters);
    if (presenters.length === 0) {
      setSelectedScreenShare(null);
      return;
    }

    setSelectedScreenShare(presenters[0]);
  }, [presenters]);

  useEffect(() => {
    if (!selectedScreenShare && !peerPresentingRaw) return;
    const data = selectedScreenShare || peerPresentingRaw;
    // console.log(peerPresentingRaw, selectedScreenShare);
    setPeerPresenting(data);
  }, [peerPresentingRaw, selectedScreenShare]);

  useEffect(() => {
    const peersRaw = peers?.filter(
      peer =>
        !peer?.isLocal &&
        (peer?.roleName !== ROLES.GUEST || peer?.roleName !== ROLES.HLS_VIEWER)
    );
    setListPeers(peersRaw);
  }, [isCabin, selectedCabinData, peers]);

  const smallTilePeers = useMemo(() => {
    const smallTilePeers = listPeers;
    // if (showPresenterInSmallTile && peerPresenting) {
    //   smallTilePeers.unshift(peerPresenting); // put presenter on first page
    // }
    return smallTilePeers;
  }, [listPeers, peerPresenting, showPresenterInSmallTile]);

  const shouldShowScreenFn = useCallback(
    peer => peerPresenting && peer,
    [peerPresenting]
  );

  return (
    <Flex
      css={{
        size: "100%",
      }}
      direction={showSidebarInBottom ? "column" : "row"}
    >
      <Flex
        css={{
          size: "100%",
        }}
        direction={{
          "@initial": showSidebarInBottom ? "row" : "column",
          "@lg": showSidebarInBottom ? "column" : "row",
        }}
      >
        <ScreenShareComponent
          amIPresenting={amIPresenting}
          peerPresenting={peerPresenting}
          peersPresenting={presenters}
          peerSharingPlaylist={peerSharingPlaylist}
        />
        {presenters.length > 1 && (
          <SmallTileScreenView
            smallTilePeers={presenters}
            shouldShowScreenFn={shouldShowScreenFn}
            showSidebarInBottom={showSidebarInBottom}
            isCabin
          />
        )}
      </Flex>
      {smallTilePeers.length && (
        <Flex
          direction={{ "@initial": "column", "@lg": "row" }}
          css={{
            overflow: "hidden",
            p: "$4 $8",
            flex: "0 0 20%",
            "@xl": {
              flex: "0 0 25%",
            },
            "@lg": {
              flex: "0 0 32%",
              p: "0 $8",
            },
          }}
        >
          <SidePane
            showSidebarInBottom={showSidebarInBottom}
            peerScreenSharing={peerPresenting}
            isPresenterInSmallTiles={showPresenterInSmallTile}
            smallTilePeers={smallTilePeers}
            totalPeers={peers?.length}
            isCabin={isCabin}
          />
        </Flex>
      )}
    </Flex>
  );
};

// Sidepane will show the camera stream of the main peer who is screensharing
// and both camera + screen(if applicable) of others
export const SidePane = ({
  isPresenterInSmallTiles,
  peerScreenSharing, // the peer who is screensharing
  smallTilePeers,
  totalPeers,
  showSidebarInBottom,
  isCabin,
}) => {
  // useEffect(() => {
  //   console.log(smallTilePeers);
  // }, [smallTilePeers]);
  // The main peer's screenshare is already being shown in center view
  const shouldShowScreenFn = useCallback(
    peer => peerScreenSharing && peer.id !== peerScreenSharing.id,
    [peerScreenSharing]
  );
  return (
    <Fragment>
      {/* {!isPresenterInSmallTiles && (
        <LargeTilePeerView
          peerScreenSharing={peerScreenSharing}
          isCabin={isCabin}
        />
      )} */}
      {smallTilePeers.length > 0 && (
        <SmallTilePeersView
          showSidebarInBottom={showSidebarInBottom}
          smallTilePeers={smallTilePeers}
          shouldShowScreenFn={shouldShowScreenFn}
          isCabin={isCabin}
        />
      )}
    </Fragment>
  );
};

const ScreenShareComponent = ({
  amIPresenting,
  peerPresenting,
  peersPresenting,
  peerSharingPlaylist,
}) => {
  const localPeerID = useHMSStore(selectLocalPeerID);
  const screenshareTrack = useHMSStore(
    selectScreenShareByPeerID(peerPresenting?.id)
  );

  if (peerSharingPlaylist) {
    return (
      <Box
        css={{
          mx: "$8",
          flex: "3 1 0",
          "@xl": {
            flex: "2 1 0",
            display: "flex",
            alignItems: "center",
          },
        }}
      >
        <VideoPlayer peerId={peerSharingPlaylist.id} />
      </Box>
    );
  }

  return (
    <Box
      css={{
        flex: peersPresenting?.length > 1 ? "3 1 75%" : "3 1 0",
        mx: "$8",
        maxHeight: peersPresenting?.length > 1 ? "75%" : "100%",
        "@xl": {
          ml: "$4",
          maxHeight: "100%",
          minHeight: 0,
        },
        "@lg": { display: peersPresenting?.length > 1 ? "none" : "" },
      }}
    >
      {peerPresenting &&
        (amIPresenting &&
        peerPresenting?.id === localPeerID &&
        ["browser", "window", "application"].includes(
          screenshareTrack?.displaySurface
        ) ? (
          <Box css={{ objectFit: "contain", h: "100%" }}>
            <ScreenshareDisplay />
          </Box>
        ) : (
          <ScreenshareTile peerId={peerPresenting?.id} />
        ))}
    </Box>
  );
};

const SmallTileScreenView = ({
  smallTilePeers,
  shouldShowScreenFn,
  showStatsOnTiles,
  showSidebarInBottom,
  isCabin,
}) => {
  return (
    <Flex
      css={{
        flex: smallTilePeers.length > 1 ? "2 1 25%" : "2 1 0",
        pb: "$10",
        width: "100%",
        "@xl": { pb: 0, height: "100%" },
      }}
    >
      {smallTilePeers && smallTilePeers?.length > 0 && (
        <VideoScreenShareList
          peers={smallTilePeers}
          maxColCount={showSidebarInBottom ? 1 : undefined}
          maxRowCount={showSidebarInBottom ? undefined : 1}
          includeScreenShareForPeer={shouldShowScreenFn}
          isCabin={isCabin}
        />
      )}
    </Flex>
  );
};

const SmallTilePeersView = ({
  smallTilePeers,
  shouldShowScreenFn,
  showStatsOnTiles,
  showSidebarInBottom,
  isCabin,
}) => {
  return (
    <Flex
      css={{
        flex: "2 1 0",
      }}
    >
      {smallTilePeers && smallTilePeers?.length > 0 && (
        <VideoList
          peers={smallTilePeers}
          maxColCount={showSidebarInBottom ? undefined : 1}
          maxRowCount={showSidebarInBottom ? 1 : undefined}
          // includeScreenShareForPeer={shouldShowScreenFn}
          isCabin={isCabin}
        />
      )}
    </Flex>
  );
};

const CabinActions = ({ peers, tile }) => {
  const {
    stateCtx: { selectedCabinData },
    setSelectedCabinData,
  } = useContext(LiveHodoContext);
  const navigate = useNavigation();
  const hmsActions = useHMSActions();
  const currVolume = useHMSStore(selectAudioTrackVolume(tile.peer?.audioTrack));
  const peerRole = tile.peer?.roleName;
  const [selectedCabin, setSelectedCabin] = useState(null);
  const [isPeerMuted, setIsPeerMuted] = useState(false);
  const [isMouseHovered, setIsMouseHovered] = useState(false);
  const [pinnedTrackId, setPinnedTrackId] = useSetAppDataByKey(
    APP_DATA.pinnedTrackId
  );

  const { roomId: urlRoomId, role: userRole } = useParams(); // from the url

  useEffect(() => {
    setSelectedCabin(selectedCabinData);
    handleToggleMuteAll(0);
    hmsActions.setVolume(100, selectedCabinData?.audioTrack);
  }, [selectedCabinData]);

  const handleToggleMuteAll = useCallback(
    (volume = 0) => {
      peers?.map(tile => {
        if (tile?.peer?.audioTrack) {
          hmsActions.setVolume(volume, tile?.peer?.audioTrack);
        }
      });
    },
    [peers?.length]
  );

  const handleSelectedCabin = useCallback(
    peer => {
      handleToggleMuteAll(0);
      hmsActions.setVolume(0, selectedCabinData?.audioTrack);
      setSelectedCabin(peer);

      if (peer?.audioTrack) {
        hmsActions.setVolume(100, peer?.audioTrack);
        let cabinURL = `/cabin/${urlRoomId}`;
        if (userRole) {
          cabinURL += `/${userRole}`;
        }
        setPinnedTrackId(peer?.audioTrack || peer?.videoTrack);
        setSelectedCabinData(peer);
        navigate(cabinURL);
      }
    },
    [handleToggleMuteAll, selectedCabinData]
  );

  const handleClearCabin = useCallback(() => {
    setSelectedCabin(null);
    setIsPeerMuted(false);
    handleToggleMuteAll(0);
    setSelectedCabinData(null);
  }, [handleToggleMuteAll]);

  const handleMutePeer = useCallback(
    peer => {
      if (peer?.audioTrack) {
        hmsActions.setVolume(
          isPeerMuted && currVolume === 0 ? 100 : 0,
          peer?.audioTrack
        );
        setIsPeerMuted(!isPeerMuted);
      }
    },
    [currVolume]
  );

  const onHoverHandler = useCallback(event => {
    setIsMouseHovered(event.type === "mouseenter");
  }, []);

  return selectedCabin &&
    (selectedCabin.roleName === ROLES.PRESENTER ||
      selectedCabin.roleName === ROLES.PANELIST) ? (
    <></>
  ) : (
    <Box
      css={{
        position: "absolute",
        top: "$6",
        left: "$6",
        marginLeft: "$2",
        marginTop: "$2",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
      }}
      onMouseEnter={onHoverHandler}
      onMouseLeave={onHoverHandler}
    >
      <Flex
        className="cabin-action"
        direction="column"
        css={{
          width: "36px",
          height: "36px",
          borderRadius: "$round",
          cursor: "pointer",
        }}
        // variant={
        //   selectedCabin && tile.peer?.id === selectedCabin?.id
        //     ? "danger"
        //     : "primary"
        // }
        onClick={() =>
          selectedCabin && tile.peer?.id === selectedCabin?.id
            ? handleClearCabin()
            : handleSelectedCabin(tile.peer)
        }
        disabled={selectedCabin && tile.peer?.id !== selectedCabin?.id}
      >
        {selectedCabin && tile.peer?.id === selectedCabin?.id ? (
          <Box css={{ margin: "auto" }}>
            <CloseIcon />
          </Box>
        ) : (
          <Box css={{ margin: "auto" }}>
            <DoorIcon />
          </Box>
        )}
      </Flex>
      {isMouseHovered && (
        <Text
          css={{
            padding: "$1",
            fontSize: "12px",
          }}
        >
          {selectedCabin && tile.peer?.id === selectedCabin?.id
            ? "Rời cabin"
            : `Nghe ${peerRole}`}
        </Text>
      )}
    </Box>
  );
};

const LargeTilePeerView = ({
  peerScreenSharing,
  showStatsOnTiles,
  isCabin,
}) => {
  return peerScreenSharing ? (
    <Box
      css={{
        position: "relative",
        flex: "1 1 0",
        minHeight: "25%",
        // py: "$4",
        "@lg": {
          mr: "$4",
          minHeight: "unset",
          // py: 0,
        },
        "@sm": {
          height: "100%",
          maxHeight: "75%",
          alignSelf: "center",
        },
      }}
    >
      <VideoTile
        showStatsOnTiles={showStatsOnTiles}
        width="100%"
        height="100%"
        peerId={peerScreenSharing.id}
      />
      {/* {isCabin ? (
        <CabinActions
          tile={{ peer: peerScreenSharing }}
          peers={[peerScreenSharing]}
        />
      ) : (
        ""
      )} */}
    </Box>
  ) : null;
};

export default ScreenShareView;
